import { useState, useMemo, useEffect } from 'react';
import { FormInstance } from 'antd';

import useMultiPortfolioStore from 'lib/stores/multiPorto';
import useSubAccounts from 'features/multi-porto/hooks/useSubAccounts';

import { iSubAccount } from 'global/Securities/types/api/multiPorto';

interface iPortfolioSelect {
  visible: boolean;
  form: FormInstance;
}

export default function usePortfolioSelect({
  visible,
  form,
}: iPortfolioSelect) {
  const [fromPortfolio, setFromPortfolio] = useState<string | null>(null);
  const [toPortfolio, setToPortfolio] = useState<string | null>(null);

  const activeAccount = useMultiPortfolioStore((s) => s.activeAccount);

  // sub accounts
  const getSubAccounts = useMultiPortfolioStore((s) => s.getSubAccounts);

  const { approvedSubAccounts } = useSubAccounts();

  const disabledSelect = useMemo(
    () => approvedSubAccounts.length <= 2,
    [approvedSubAccounts.length],
  );

  const mapPortfolioOptions = (_subAccounts: iSubAccount[], exclude?: string) =>
    _subAccounts
      .filter(({ account_number }) => account_number !== exclude)
      .map((acc) => ({
        label: acc.portfolio_info.portfolio_name,
        value: acc.account_number,
        isMain: acc.is_main_account,
      }));

  const fromPortfolioOptions = useMemo(
    () => mapPortfolioOptions(approvedSubAccounts, toPortfolio),
    [approvedSubAccounts.length, toPortfolio, visible],
  );

  const toPortfolioOptions = useMemo(
    () => mapPortfolioOptions(approvedSubAccounts, fromPortfolio),
    [approvedSubAccounts.length, fromPortfolio, visible],
  );

  const handleChangeFromPortfolio = (value: string) => {
    setFromPortfolio(value);

    // if value is selected in toPortfolio, remove it from fromPortfolio
    if (toPortfolio === value) {
      setToPortfolio(null);
      form.setFieldsValue({ toPortfolio: null });
      return;
    }

    const options = mapPortfolioOptions(approvedSubAccounts, value) || [];
    const availableToOptions = options.filter(
      (subAccount) => subAccount.value !== value,
    );

    if (availableToOptions.length === 1) {
      form.setFieldsValue({ toPortfolio: availableToOptions[0].value });
      setToPortfolio(availableToOptions[0].value);
    }
  };

  const handleChangeToPortfolio = (value: string) => {
    setToPortfolio(value);

    // if value is selected in fromPortfolio, remove it from toPortfolio
    if (fromPortfolio === value) {
      setFromPortfolio(null);
      form.setFieldsValue({ fromPortfolio: null });
      return;
    }

    const options = mapPortfolioOptions(approvedSubAccounts, value) || [];
    const availableFromOptions = options.filter(
      (subAccount) => subAccount.value !== value,
    );

    if (availableFromOptions.length === 1) {
      form.setFieldsValue({ fromPortfolio: availableFromOptions[0].value });
      setFromPortfolio(availableFromOptions[0].value);
    }
  };

  const handleSwitchPortfolio = () => {
    if (!fromPortfolio && !toPortfolio) return;

    if (fromPortfolio && toPortfolio) {
      const tempFromPortfolio = fromPortfolio;
      const tempToPortfolio = toPortfolio;

      setFromPortfolio(tempToPortfolio);
      form.setFieldsValue({ fromPortfolio: tempToPortfolio });
      setToPortfolio(tempFromPortfolio);
      form.setFieldsValue({ toPortfolio: tempFromPortfolio });
    }

    if (fromPortfolio && !toPortfolio) {
      setToPortfolio(fromPortfolio);
      form.setFieldsValue({ toPortfolio: fromPortfolio });
      setFromPortfolio(null);
      form.setFieldsValue({ fromPortfolio: null });
    }

    if (!fromPortfolio && toPortfolio) {
      setFromPortfolio(toPortfolio);
      form.setFieldsValue({ fromPortfolio: toPortfolio });
      setToPortfolio(null);
      form.setFieldsValue({ toPortfolio: null });
    }
  };

  const handleReset = () => {
    setFromPortfolio(null);
    setToPortfolio(null);
    form.resetFields();
  };

  // Set default fromPortfolio and toPortfolio
  useEffect(() => {
    if (!visible) return;

    if (approvedSubAccounts.length === 2 && !fromPortfolio && !toPortfolio) {
      const fromAccount = approvedSubAccounts.find(
        (acc) => acc.account_number === activeAccount,
      );
      const toAccount = approvedSubAccounts.find(
        (acc) => acc.account_number !== activeAccount,
      );

      setFromPortfolio(fromAccount?.account_number || null);
      form.setFieldsValue({
        fromPortfolio: fromAccount?.account_number || null,
      });
      setToPortfolio(toAccount?.account_number || null);
      form.setFieldsValue({ toPortfolio: toAccount?.account_number || null });
    }

    if (approvedSubAccounts.length > 2 && !fromPortfolio) {
      const fromAccount = approvedSubAccounts.find(
        (acc) => acc.account_number === activeAccount,
      );

      setFromPortfolio(fromAccount?.account_number || null);
      form.setFieldsValue({
        fromPortfolio: fromAccount?.account_number || null,
      });
    }
  }, [approvedSubAccounts.length, activeAccount, visible]);

  // Get sub accounts data when modal is visible
  useEffect(() => {
    if (visible) getSubAccounts();
  }, [visible, getSubAccounts]);

  return {
    fromPortfolio,
    fromPortfolioOptions,
    toPortfolio,
    toPortfolioOptions,
    disabledSelect,
    onChangeFromPortfolio: handleChangeFromPortfolio,
    onChangeToPortfolio: handleChangeToPortfolio,
    onSwitchPortfolio: handleSwitchPortfolio,
    onReset: handleReset,
  };
}
